import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ThemedPaper } from '@/components/ThemedPaper';
import { TimeslotGrid } from '@/components/TimeslotGrid';
import { LoadingButton } from '@/components/LoadingButton';
import { useReservationStore } from '@/store/reservationStore';
import { useTimeslots } from '@/hooks/useTimeslots';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { useDomain, useReservePath } from '@/hooks/useDomain';

/**
 * Second step (when no areas): Select a time slot
 */
export function TimeslotSelection() {
  const navigate = useNavigate();
  const domain = useDomain();
  const reservePath = useReservePath();
  const { t } = useTranslation();
  const { date, people, children, selectedTimeslot, setTimeslot, setDate, setPeople } = useReservationStore();
  const { data: timeslots, isLoading } = useTimeslots(domain, date, people, children);
  const { data: storeInfo } = useStoreInfo(domain);

  const handleBack = () => {
    // Clear selections to prevent auto-navigation loop
    setDate(null);
    setPeople(2);
    setTimeslot(null);
    navigate(reservePath);
  };

  const handleContinue = () => {
    if (selectedTimeslot) {
      navigate(`${reservePath}/submit`);
    }
  };

  // Filter out slots with areas (shouldn't happen, but just in case)
  const availableSlots = (timeslots?.filter((slot) => !slot.area_uuid) || []) as any[];

  return (
    <ThemedPaper>
      <Box p={4}>
        <Box mb={3}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            {t('common.back')}
          </Button>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          {t('timeslotSelection.title')}
        </Typography>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          {date?.toLocaleDateString()} • {t('partySize.adult', { count: people })}
          {children > 0 && `, ${t('partySize.child', { count: children })}`}
        </Typography>

        <Box mt={3}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>{t('timeslotSelection.loadingTimes')}</Typography>
            </Box>
          ) : availableSlots.length > 0 ? (
            <TimeslotGrid
              timeslots={availableSlots}
              selectedTimeslot={selectedTimeslot}
              onSelectTimeslot={setTimeslot}
              showDuration={storeInfo?.show_duration ?? false}
            />
          ) : (
            <Typography color="text.secondary">
              {t('timeslotSelection.noTimeslots')}
            </Typography>
          )}
        </Box>

        <Box mt={4}>
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleContinue}
            disabled={!selectedTimeslot}
            endIcon={<ArrowForward />}
          >
            {t('common.continue')}
          </LoadingButton>
        </Box>
      </Box>
    </ThemedPaper>
  );
}
